import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  type Node,
  type Edge,
  type NodeTypes,
  Handle,
  Position,
  type NodeProps,
  ReactFlowProvider,
} from "reactflow";
import dagre from "dagre";
import "reactflow/dist/style.css";
import {
  getTopology,
  type TopologyNode,
  type TopologyEdge,
} from "@/api/endpoints/topology";
import { Server, Network, Globe, Share2, X } from "lucide-react";

// ─── Custom node components ──────────────────────────────────────────────────
// Each type has its own shape so a glance tells you the layer: sites are big
// cards with service list, pve_nodes show CPU %, VMs get a status-coloured
// pill, VLANs get a tag badge.

function SiteNode({ data }: NodeProps) {
  const services = (data.services as string[] | undefined) ?? [];
  return (
    <div className="rounded-xl border-2 border-blue-400 bg-blue-950/80 p-3 shadow-lg min-w-[180px]">
      <Handle type="target" position={Position.Left} className="!bg-blue-400" />
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-blue-300" />
        <span className="font-semibold text-blue-100">{data.label}</span>
      </div>
      {data.ip && (
        <div className="mt-1 font-mono text-xs text-blue-300/70">
          {data.ip as string}
        </div>
      )}
      {services.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {services.slice(0, 3).map((s) => (
            <span
              key={s}
              className="rounded bg-blue-900/60 px-1.5 py-0.5 text-[10px] text-blue-200"
            >
              {s}
            </span>
          ))}
          {services.length > 3 && (
            <span className="text-[10px] text-blue-300/70">
              +{services.length - 3}
            </span>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Right} className="!bg-blue-400" />
    </div>
  );
}

function PveNodeNode({ data }: NodeProps) {
  const cpu = (data.cpu as number | undefined) ?? 0;
  const mem = (data.mem as number | undefined) ?? 0;
  const maxmem = (data.maxmem as number | undefined) ?? 1;
  const status = (data.status as string | undefined) ?? "unknown";
  const online = status === "online";
  const cpuPct = Math.round(cpu * 100);
  const memPct = Math.round((mem / maxmem) * 100);
  return (
    <div
      className={`rounded-lg border-2 bg-emerald-950/80 p-2.5 shadow min-w-[160px] ${
        online ? "border-emerald-400" : "border-red-400"
      }`}
    >
      <Handle type="target" position={Position.Left} className="!bg-emerald-400" />
      <div className="flex items-center gap-1.5">
        <Server className="h-3.5 w-3.5 text-emerald-300" />
        <span className="font-semibold text-emerald-100 text-sm">
          {data.label}
        </span>
        <span
          className={`ml-auto h-2 w-2 rounded-full ${
            online ? "bg-emerald-400" : "bg-red-500"
          }`}
        />
      </div>
      <div className="mt-1.5 space-y-1">
        <MiniBar label="CPU" pct={cpuPct} />
        <MiniBar label="RAM" pct={memPct} />
      </div>
      <Handle type="source" position={Position.Right} className="!bg-emerald-400" />
    </div>
  );
}

function MiniBar({ label, pct }: { label: string; pct: number }) {
  const color =
    pct >= 85 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-7 text-[10px] text-emerald-300/80">{label}</span>
      <div className="h-1.5 flex-1 rounded-full bg-gray-800 overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <span className="w-8 text-right text-[10px] text-emerald-300/80">
        {pct}%
      </span>
    </div>
  );
}

function VmNode({ data }: NodeProps) {
  const status = (data.status as string | undefined) ?? "unknown";
  const tier = (data.service_tier as string | undefined) ?? "?";
  const vmid = data.vmid as number | undefined;
  const statusColors: Record<string, string> = {
    running: "border-emerald-400 bg-emerald-950/60 text-emerald-100",
    stopped: "border-gray-600 bg-gray-900/60 text-gray-400",
    error: "border-red-500 bg-red-950/60 text-red-200",
    provisioning: "border-blue-400 bg-blue-950/60 text-blue-200",
  };
  const cls = statusColors[status] ?? statusColors.stopped;
  const tierColors: Record<string, string> = {
    A: "bg-gray-700 text-gray-200",
    B: "bg-blue-700 text-blue-100",
    C: "bg-purple-700 text-purple-100",
  };
  return (
    <div
      className={`rounded-md border px-2.5 py-1.5 shadow-sm min-w-[150px] ${cls}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-gray-500" />
      <div className="flex items-center gap-1.5">
        <span className="truncate font-medium text-sm">{data.label}</span>
        <span
          className={`ml-auto rounded px-1 py-0 text-[9px] font-bold ${tierColors[tier] ?? tierColors.A}`}
        >
          {tier}
        </span>
      </div>
      <div className="flex items-center justify-between text-[10px] opacity-70">
        {vmid != null && <span>#{vmid}</span>}
        <span>{status}</span>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-gray-500" />
    </div>
  );
}

function VlanNode({ data }: NodeProps) {
  return (
    <div className="rounded-md border-2 border-orange-500 bg-orange-950/60 px-2.5 py-1.5 shadow-sm min-w-[140px]">
      <Handle type="target" position={Position.Left} className="!bg-orange-500" />
      <div className="flex items-center gap-1.5">
        <Network className="h-3 w-3 text-orange-300" />
        <span className="text-sm font-medium text-orange-100">{data.label}</span>
      </div>
    </div>
  );
}

const nodeTypes: NodeTypes = {
  site: SiteNode,
  pve_node: PveNodeNode,
  vm: VmNode,
  vlan: VlanNode,
};

// ─── Layout (dagre) ──────────────────────────────────────────────────────────
// Hierarchical top-down: Site → PVE Node → VM (or VLAN). dagre places nodes
// so edges mostly flow downward with minimal crossings; way more readable
// than the fixed-column layout the previous version used.

const NODE_SIZES: Record<string, { w: number; h: number }> = {
  site: { w: 200, h: 100 },
  pve_node: { w: 180, h: 80 },
  vm: { w: 170, h: 60 },
  vlan: { w: 160, h: 45 },
};

function layoutWithDagre(
  nodes: Node[],
  edges: Edge[]
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 25, ranksep: 110, marginx: 20, marginy: 20 });

  for (const n of nodes) {
    const type = (n.type as string) ?? "vm";
    const size = NODE_SIZES[type] ?? NODE_SIZES.vm;
    g.setNode(n.id, { width: size.w, height: size.h });
  }
  for (const e of edges) {
    // Skip IPsec edges from the layout (they're mesh-like and would
    // bloat rank placement); render them as overlays with smoothstep.
    // Skip mesh-like edges from layout — render them as overlays.
    // ipsec is a site mesh; management/monitors connect bastion+monitoring
    // to every site and would otherwise drag those nodes far off rank.
    if (!["ipsec", "management", "monitors"].includes(e.data?.kind ?? "")) {
      g.setEdge(e.source, e.target);
    }
  }
  dagre.layout(g);

  const laidOut = nodes.map((n) => {
    const pos = g.node(n.id);
    if (!pos) return n;
    return {
      ...n,
      position: {
        x: pos.x - (NODE_SIZES[(n.type as string) ?? "vm"]?.w ?? 170) / 2,
        y: pos.y - (NODE_SIZES[(n.type as string) ?? "vm"]?.h ?? 60) / 2,
      },
    };
  });
  return { nodes: laidOut, edges };
}

// ─── Graph → ReactFlow converter ─────────────────────────────────────────────

const EDGE_COLORS: Record<string, string> = {
  ipsec: "#f97316",
  member: "#3b82f6",
  hosts: "#10b981",
  membership: "#a855f7",
  replica: "#eab308",
  replica_cross: "#ef4444",
  management: "#94a3b8",
  monitors: "#06b6d4",
};

function toReactFlow(
  graph: { nodes: TopologyNode[]; edges: TopologyEdge[] },
  filters: Filters
): { nodes: Node[]; edges: Edge[] } {
  const visibleNodeIds = new Set<string>();
  const rfNodes: Node[] = [];
  for (const n of graph.nodes) {
    if (!passesFilters(n, filters)) continue;
    visibleNodeIds.add(n.id);
    rfNodes.push({
      id: n.id,
      position: { x: 0, y: 0 }, // dagre overrides
      data: { label: n.label, ...n.data },
      type: n.type,
    });
  }

  const rfEdges: Edge[] = [];
  for (const e of graph.edges) {
    if (!visibleNodeIds.has(e.source) || !visibleNodeIds.has(e.target)) continue;
    if (!filters.showReplicas && (e.type === "replica" || e.type === "replica_cross")) {
      continue;
    }
    const color = EDGE_COLORS[e.type] ?? "#6b7280";
    const isReplica = e.type === "replica" || e.type === "replica_cross";
    rfEdges.push({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label ?? undefined,
      labelStyle: { fill: color, fontSize: 11, fontWeight: 500 },
      labelBgPadding: [4, 2],
      labelBgBorderRadius: 3,
      labelBgStyle: { fill: "#111827", fillOpacity: 0.9 },
      type: ["ipsec", "management", "monitors"].includes(e.type) ? "smoothstep" : "default",
      animated: e.type === "ipsec",
      data: { kind: e.type },
      style: {
        stroke: color,
        strokeWidth: isReplica ? 1.5 : 2,
        strokeDasharray: isReplica ? "6 4" : undefined,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
    });
  }
  return layoutWithDagre(rfNodes, rfEdges);
}

// ─── Filters ────────────────────────────────────────────────────────────────

interface Filters {
  tenantId: number | null; // null = all
  tier: "all" | "A" | "B" | "C";
  status: "all" | "running" | "stopped";
  showReplicas: boolean;
  showVlans: boolean;
}

function passesFilters(n: TopologyNode, f: Filters): boolean {
  if (n.type === "vlan" && !f.showVlans) return false;
  if (n.type !== "vm") return true; // sites + pve_nodes always shown
  if (f.tenantId !== null && n.data.tenant_id !== f.tenantId) return false;
  if (f.tier !== "all" && n.data.service_tier !== f.tier) return false;
  if (f.status !== "all" && n.data.status !== f.status) return false;
  return true;
}

// ─── Detail panel ───────────────────────────────────────────────────────────

function DetailPanel({
  node,
  onClose,
}: {
  node: TopologyNode | null;
  onClose: () => void;
}) {
  if (!node) return null;
  return (
    <div className="absolute right-0 top-0 z-20 h-full w-80 overflow-y-auto border-l border-gray-800 bg-gray-950/95 p-4 text-gray-100 backdrop-blur">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {node.type.replace("_", " ")}
          </div>
          <h2 className="text-lg font-semibold">{node.label}</h2>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          aria-label="Close details"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <dl className="space-y-1.5 text-sm">
        {Object.entries(node.data).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="min-w-[90px] text-xs uppercase tracking-wide text-gray-500">
              {k.replace(/_/g, " ")}
            </dt>
            <dd className="break-words font-mono text-xs text-gray-200">
              {formatValue(v)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

// ─── Legend ─────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div className="pointer-events-none absolute bottom-20 left-4 z-10 rounded-md border border-gray-800 bg-gray-950/85 p-2.5 text-xs text-gray-300 backdrop-blur">
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-emerald-500" /> hosts
      </div>
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-purple-500" /> VLAN membership
      </div>
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-yellow-500" /> replica (same site)
      </div>
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-red-500" /> replica cross-site (DR)
      </div>
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-orange-500" /> IPsec
      </div>
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-slate-400" /> SSH bastion
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-4 bg-cyan-500" /> Monitoring
      </div>
    </div>
  );
}

// ─── Filter bar ─────────────────────────────────────────────────────────────

function FilterBar({
  filters,
  setFilters,
  tenantOptions,
  counts,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  tenantOptions: Array<{ id: number; label: string }>;
  counts: { sites: number; nodes: number; vms: number; vlans: number };
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-gray-800 bg-gray-950/90 px-4 py-2 text-xs text-gray-300 backdrop-blur">
      <div className="flex items-center gap-1.5">
        <Share2 className="h-3.5 w-3.5 text-gray-500" />
        <span className="font-semibold text-gray-100">Topology</span>
        <span className="text-gray-500">
          · {counts.sites} sites · {counts.nodes} PVE · {counts.vms} VMs ·{" "}
          {counts.vlans} VLANs
        </span>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1">
          tenant
          <select
            className="rounded bg-gray-900 px-2 py-0.5 text-gray-100"
            value={filters.tenantId ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                tenantId: e.target.value ? Number(e.target.value) : null,
              })
            }
          >
            <option value="">all</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          tier
          <select
            className="rounded bg-gray-900 px-2 py-0.5 text-gray-100"
            value={filters.tier}
            onChange={(e) =>
              setFilters({ ...filters, tier: e.target.value as Filters["tier"] })
            }
          >
            <option value="all">all</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          status
          <select
            className="rounded bg-gray-900 px-2 py-0.5 text-gray-100"
            value={filters.status}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: e.target.value as Filters["status"],
              })
            }
          >
            <option value="all">all</option>
            <option value="running">running</option>
            <option value="stopped">stopped</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={filters.showReplicas}
            onChange={(e) =>
              setFilters({ ...filters, showReplicas: e.target.checked })
            }
          />
          replicas
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={filters.showVlans}
            onChange={(e) =>
              setFilters({ ...filters, showVlans: e.target.checked })
            }
          />
          VLANs
        </label>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

function TopologyInner() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["topology"],
    queryFn: getTopology,
    refetchInterval: 30_000,
  });

  const [filters, setFilters] = useState<Filters>({
    tenantId: null,
    tier: "all",
    status: "all",
    showReplicas: true,
    showVlans: true,
  });
  const [selected, setSelected] = useState<TopologyNode | null>(null);

  const tenantOptions = useMemo(() => {
    if (!data) return [];
    const seen = new Map<number, string>();
    for (const n of data.nodes) {
      if (n.type === "vm" && typeof n.data.tenant_id === "number") {
        seen.set(n.data.tenant_id, `tenant #${n.data.tenant_id}`);
      }
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [data]);

  const { nodes, edges } = useMemo(
    () =>
      data ? toReactFlow(data, filters) : { nodes: [] as Node[], edges: [] as Edge[] },
    [data, filters]
  );

  const counts = useMemo(() => {
    if (!data) return { sites: 0, nodes: 0, vms: 0, vlans: 0 };
    const c = { sites: 0, nodes: 0, vms: 0, vlans: 0 };
    for (const n of data.nodes) {
      if (n.type === "site") c.sites++;
      else if (n.type === "pve_node") c.nodes++;
      else if (n.type === "vm") c.vms++;
      else if (n.type === "vlan") c.vlans++;
    }
    return c;
  }, [data]);

  const handleNodeClick = useCallback(
    (_e: React.MouseEvent, node: Node) => {
      const raw = data?.nodes.find((n) => n.id === node.id);
      setSelected(raw ?? null);
    },
    [data]
  );

  // Close detail panel on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (isLoading) {
    return <div className="p-6 text-gray-400">Loading topology…</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-red-400">
        Failed to load topology: {String(error)}
      </div>
    );
  }

  return (
    <div className="relative flex h-[calc(100vh-4rem)] w-full flex-col bg-gray-950">
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        tenantOptions={tenantOptions}
        counts={counts}
      />
      <div className="relative flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable
          onNodeClick={handleNodeClick}
          minZoom={0.2}
          maxZoom={2}
        >
          <Background color="#1f2937" gap={24} />
          <Controls className="!bg-gray-800 !border-gray-700" />
          <MiniMap
            nodeColor={(n) =>
              (
                {
                  site: "#1e3a8a",
                  pve_node: "#065f46",
                  vm: "#1f2937",
                  vlan: "#7c2d12",
                } as Record<string, string>
              )[(n.type as string) ?? "vm"] ?? "#374151"
            }
            maskColor="rgba(0,0,0,0.6)"
            className="!bg-gray-900 !border-gray-700"
          />
        </ReactFlow>
        <Legend />
        <DetailPanel node={selected} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
}

export default function Topology() {
  return (
    <ReactFlowProvider>
      <TopologyInner />
    </ReactFlowProvider>
  );
}
